import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { A11yModule } from '@angular/cdk/a11y';
import { AgentIaService } from './agent-ia.service';
import { AgentIaState } from './agent-ia.state';
import { AgentMarkdownComponent } from './agent-markdown.component';
import { AgentVoiceInput } from './agent-audio';
import { AgentActivityBubbleComponent } from './agent-activity-bubble.component';
import { AGENT_PANEL_INFO, AgentFollowService } from './agent-follow.service';
import { AgentActionCardComponent } from './agent-action-card.component';
import { AgentApprovalsService } from './agent-approvals.service';
import { APPROVAL_PATH, ApprovalAction, INBOX_PATH, opensApprovalPage } from './agent-action-card';
import { badgeLabel, badgeText } from './agent-approvals';
import {
  ACTION_STATUS_LABELS, AGENT_LIMITS, AGENT_UPLOAD_ACCEPT, AgentAction, AgentConversation, AgentFile, AgentMessage, FILE_STATUS_LABELS, actionCard, actionTypeLabel,
  changesetKindLabel, changesetNeedsReview, conversationAuthor, friendlyError, hideSupportWidget, messageParts, routeContext, runErrorMessage
} from './agent-ia.models';

@Component({
  selector: 'app-agent-ia', standalone: true,
  imports: [CommonModule, FormsModule, A11yModule, RouterLink, AgentMarkdownComponent, AgentActivityBubbleComponent, AgentActionCardComponent],
  templateUrl: './agent-ia.component.html', styleUrls: ['./agent-ia.component.scss'], changeDetection: ChangeDetectionStrategy.OnPush
})
export class AgentIaComponent implements OnInit, OnDestroy {
  @Output() openedChange = new EventEmitter<boolean>();
  @ViewChild('panel') panel?: ElementRef<HTMLElement>;
  @ViewChild('composer') composer?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('thread') thread?: ElementRef<HTMLElement>;
  @ViewChild('launcher') launcher?: ElementRef<HTMLButtonElement>;
  readonly state: AgentIaState;
  readonly voice: AgentVoiceInput;
  private voiceContext: ReturnType<typeof routeContext> = {};
  readonly fileStatusLabels = FILE_STATUS_LABELS;
  readonly actionStatusLabels = ACTION_STATUS_LABELS;
  readonly actionTypeLabel = actionTypeLabel;
  readonly changesetKindLabel = changesetKindLabel;
  readonly changesetNeedsReview = changesetNeedsReview;
  readonly accept = AGENT_UPLOAD_ACCEPT;
  readonly maxChars = AGENT_LIMITS.messageChars;
  readonly panelInfo = AGENT_PANEL_INFO; // panel links never pause the follow mode
  mobile = window.innerWidth <= 900;
  dragging = false;
  downloading = new Set<string>();
  private destroyed = false;
  private scrollQueued = false;
  private followMessages = true;
  private restoreSupport: () => void = () => {};
  private partsCache = new Map<string, { content: string; parts: ReturnType<typeof messageParts> }>();
  private cards = new WeakMap<AgentAction, { conversation: AgentConversation | null; card: ApprovalAction }>();
  private unwatchApprovals: () => void = () => {};
  readonly inboxPath = INBOX_PATH;

  constructor(private api: AgentIaService, private router: Router, private cd: ChangeDetectorRef, follow: AgentFollowService,
    private approvals?: AgentApprovalsService) {
    // Follow mode: fresh events drive the follow engine; pages save their edits before send / answer / approve / undo.
    this.state = new AgentIaState(api, () => this.changed(), { onFollow: signal => follow.signal(signal), onReset: () => follow.reset(), flush: () => follow.flushPages() });
    this.voice = new AgentVoiceInput((audio, signal) => this.state.transcribe(audio, this.voiceContext, signal), () => {
      this.state.audioBusy = this.voice?.busy || false; this.changed();
    });
  }
  get context() {
    const params: Record<string, unknown> = {};
    let route = this.router.routerState.snapshot.root;
    while (route) { Object.assign(params, route.params); route = route.firstChild!; }
    return routeContext(params);
  }
  get contextLabel() {
    const conversation = this.state.conversation;
    if (conversation?.projectTitle && conversation.scope.projectId) return conversation.projectTitle;
    if (conversation) return conversation.scope.dpgfId ? 'DPGF liée à la conversation' : conversation.scope.projectId ? 'Projet lié à la conversation' : 'Conversation sans projet';
    const context = this.context;
    return context.dpgfId ? 'DPGF ouverte' : context.projectId ? 'Projet ouvert' : 'Espace architecte';
  }
  get contextIcon() {
    const scope = this.state.conversation?.scope || this.context;
    return scope.dpgfId ? 'pi pi-file' : scope.projectId ? 'pi pi-folder' : 'pi pi-briefcase';
  }
  get projectLink() { const id = this.state.conversation?.scope.projectId; return id ? ['/architect/ce-project', id] : null; }
  get dpgfLink() { const id = this.state.conversation?.scope.dpgfId; return id ? ['/architect/dpgf', id] : null; }
  get canCreateProject() { const scope = this.state.conversation?.scope || this.context; return !scope.projectId && !scope.dpgfId; }
  // A conversation keeps its scope when navigating; bootstrap only binds a newly created project.
  get otherDpgf() {
    const linked = this.state.conversation?.scope.dpgfId, opened = this.context.dpgfId;
    return Boolean(linked && opened && linked !== opened);
  }
  get steps() { return this.state.steps.slice(-8); }
  get runError() { return runErrorMessage(this.state.run?.error); }
  get busyLauncher() { return this.state.runActive || this.state.waiting || this.state.pendingActions.length > 0; }
  // « À valider » (06-FRONT.md §5.4): proposals waiting for my decision, all conversations and connections included.
  get approvalsCount() { return this.approvals?.count || 0; }
  get approvalsBadge() { return badgeText(this.approvalsCount); }
  get approvalsLabel() { return badgeLabel(this.approvalsCount); }
  get launcherLabel() { return this.approvalsCount ? `Ouvrir Agent IA, ${badgeLabel(this.approvalsCount)}` : 'Ouvrir Agent IA'; }
  // Shared action card (agent-action-card), one object per action value (OnPush input).
  cardOf(action: AgentAction): ApprovalAction {
    const conversation = this.state.conversation;
    const cached = this.cards.get(action);
    if (cached && cached.conversation === conversation) return cached.card;
    const card = actionCard(action, conversation);
    this.cards.set(action, { conversation, card });
    return card;
  }
  // A proposal of an AI connection, or a type of phase 3, is decided on its approval page (render, code, decision token);
  // the agent's own six types keep their buttons in the panel.
  opensApprovalPage(action: AgentAction) { return opensApprovalPage(action, this.state.conversation); }
  approvalLink(action: AgentAction) { return [APPROVAL_PATH, action.id]; }
  // Journal of an AI connection (docs/mcp/06-FRONT.md §7): author badge, no welcome screen, composer replaced by a notice.
  get mcpAuthor() { return conversationAuthor(this.state.conversation); }
  authorOf(conversation: AgentConversation) { return conversationAuthor(conversation); }
  get welcome() { return !this.state.loading && !this.state.messages.length && !this.state.run && !this.mcpAuthor; }

  private changed() {
    if (this.destroyed) return;
    this.cd.markForCheck();
    if (this.followMessages && !this.scrollQueued) {
      this.scrollQueued = true;
      requestAnimationFrame(() => {
        this.scrollQueued = false;
        const thread = this.thread?.nativeElement;
        if (!this.destroyed && thread) thread.scrollTop = this.state.messages.length || this.state.run ? thread.scrollHeight : 0;
      });
    }
  }
  // Run started from this tab before a reload: followed again in the background.
  ngOnInit() {
    void this.state.resume();
    if (this.approvals) this.unwatchApprovals = this.approvals.watch(() => this.changed());
  }
  toggle() { this.state.opened ? this.close() : this.open(); }
  open() {
    if (this.state.opened) return;
    this.restoreSupport = hideSupportWidget((window as any).Tawk_API); this.followMessages = true; this.state.open(); this.openedChange.emit(true); this.approvals?.refresh();
    setTimeout(() => { if (!this.destroyed && this.state.opened && !this.mobile) this.composer?.nativeElement.focus(); });
  }
  close() { this.voice.cancel(); this.state.close(); this.restoreSupport(); this.openedChange.emit(false); setTimeout(() => this.launcher?.nativeElement.focus()); }
  newConversation() { this.voice.cancel(); this.followMessages = true; this.state.newConversation(); setTimeout(() => this.composer?.nativeElement.focus()); }
  select(id: string) { this.voice.cancel(); this.followMessages = true; this.state.select(id); }
  showHistory() { this.voice.cancel(); this.state.view = 'history'; this.state.loadHistory(); }
  get canDictate() { return !this.state.loading && !this.state.sending && !this.state.uploading && !this.state.waiting && !this.state.runActive && !this.state.readOnly; }
  toggleMicrophone() {
    if (this.voice.phase === 'recording') { this.voice.stop(); return; }
    if (this.voice.busy || !this.canDictate) return;
    this.voiceContext = { ...this.context }; void this.voice.start();
  }
  retry() { this.state.view === 'history' || !this.state.conversation ? this.state.loadHistory() : this.state.refresh(); }
  useSuggestion(value: string) { this.state.draft.text = value; this.composer?.nativeElement.focus(); }
  send() { this.followMessages = true; this.state.send(this.context); }
  answer() { this.followMessages = true; this.state.answer(this.state.answerContent); }
  selectAnswerOption(option: string) { this.followMessages = false; this.state.selectAnswerOption(option); }
  composerKey(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) { event.preventDefault(); this.send(); }
  }
  @HostListener('document:keydown', ['$event']) keydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.state.opened && (this.mobile || this.panel?.nativeElement.contains(event.target as Node))) { event.preventDefault(); this.close(); }
  }
  @HostListener('window:resize') resize() { this.mobile = window.innerWidth <= 900; }
  @HostListener('document:visibilitychange') visibility() {
    this.approvals?.visibilityChanged();
    if (document.visibilityState === 'visible') this.state.wake();
  }
  trackId(index: number, item: { id?: string; seq?: number }) { return item.id || item.seq || index; }
  parts(message: AgentMessage) {
    const cached = this.partsCache.get(message.id);
    if (cached?.content === message.content) return cached.parts;
    const parts = messageParts(message.content || '');
    if (this.partsCache.size > 200) this.partsCache.clear();
    this.partsCache.set(message.id, { content: message.content, parts }); return parts;
  }
  summaryParts() { return messageParts(this.state.summaryOnly); }
  fileIcon(file: AgentFile) {
    if (file.status === 'failed') return 'pi pi-exclamation-triangle';
    if (file.status !== 'ready') return 'pi pi-spin pi-spinner';
    return /^image\//.test(file.mimeType) ? 'pi pi-image' : 'pi pi-file';
  }
  filesChanged(event: Event) { const input = event.target as HTMLInputElement; this.state.upload(Array.from(input.files || []), this.context); input.value = ''; }
  dragOver(event: DragEvent) {
    if (!event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault();
    // Journal of an AI connection: nothing is attached there (no drop zone, and the browser does not open the file instead).
    if (this.state.readOnly) { this.dragging = false; event.dataTransfer.dropEffect = 'none'; return; }
    this.dragging = true; event.dataTransfer.dropEffect = 'copy';
  }
  dragLeave(event: DragEvent) { if (!this.panel?.nativeElement.contains(event.relatedTarget as Node)) this.dragging = false; }
  drop(event: DragEvent) { event.preventDefault(); this.dragging = false; this.state.upload(Array.from(event.dataTransfer?.files || []), this.context); }
  onScroll() { const element = this.thread?.nativeElement; if (element) this.followMessages = element.scrollHeight - element.scrollTop - element.clientHeight < 80; }
  size(bytes: number) { return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} Mo` : `${Math.max(1, Math.ceil(bytes / 1024))} Ko`; }
  async download(file: AgentFile) {
    const conversationId = this.state.conversation?.id;
    if (!file.id || this.downloading.has(file.id)) return;
    this.downloading.add(file.id); this.changed();
    try {
      const blob = await this.api.download(file.id);
      if (this.destroyed) return;
      const url = URL.createObjectURL(blob), anchor = document.createElement('a');
      anchor.href = url; anchor.download = file.name || 'document'; anchor.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) { if (this.state.conversation?.id === conversationId) this.state.error = friendlyError(error, 'download'); }
    finally { this.downloading.delete(file.id); this.changed(); }
  }
  ngOnDestroy() { this.destroyed = true; this.unwatchApprovals(); this.voice.dispose(); this.state.dispose(); this.restoreSupport(); this.openedChange.emit(false); }
}
